<template>
  <div>
    <div
      v-show="isPageLoad"
      class="pageProgress"
    ></div>
    <VitePwaManifest v-if="isPWADomain" />
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
  </div>
</template>

<script lang="ts" setup>
  import { initValibotMessages } from '@/utils/valibot'

  import { Query } from './core/constants/route'
  import { cookieService, storageService } from './core/services'
  import { countryList } from './public/static-media-frontend/languages'

  onErrorCaptured(
    (error, instance) =>
      !(
        error instanceof DOMException &&
        error.name === 'AbortError' &&
        (error.message.startsWith('DI call cancelled') || instance?.$.isUnmounted)
      ),
  )

  const isPageLoad = useState('pageLoad')

  const app = useNuxtApp()
  app.hook('page:loading:end', () => {
    isPageLoad.value = false
  })

  useScreenOld({
    desktop: '(min-width: 1152px)',
    laptop: '(max-width: 1550px)',
    mobile: '(max-width: 768px)',
    tablet: '(max-width: 1152px) and (min-width: 768px)',
  })

  useHrefLang()

  const route = useRoute()
  const isPWADomain = computed(() => {
    if (import.meta.client) {
      return window.location.host === 'spinarium.app'
    }
    return false
  })
  const { availableLocale, locale, setLocale, t } = useLocale()
  const dialog = useDialog()
  const notify = useNotify()
  const userStore = useBrickUserStore()
  const tournamentsStore = useBrickTournamentsStore()
  const isBot = useIsBot()

  const confirmEmailHttp = await app.$di.root.confirmEmail({ lazy: true })

  const rootStore = useBrickRootStore()
  const { restoreTimezone } = useTimezone()

  const router = useRouter()
  router.beforeEach(() => {
    if (rootStore.userGeo.hasRestriction) {
      dialog.open('regionRestriction')
      return false
    }
    return
  })

  useSocket('open_modal_message', async (data) => {
    if (userStore.user?.id === data.user_id && data.modal) {
      if (dialog.name === 'bonusCompleted') {
        await dialog.closePromise
      }
      if (data.modal === 'becameVip') {
        userStore.setUserIsVipMember(true)
      }
      if (data.modal === 'trustpilotReview') {
        setTimeout(async () => {
          await dialog.closePromise
          dialog.open(data.modal, data.payload)
        }, 10000)
      } else {
        dialog.open(data.modal, data.payload)
      }
    }
  })

  const tournamentListHttp = await app.$di.tournament.getTournamentList({
    effect(data) {
      if (data?.status === 'success') {
        tournamentsStore.updateList(data.list)
      }
    },
    lazy: true,
  })
  useSocket('tournament_finish', async (data) => {
    if (data.tournament_finish) {
      tournamentsStore.setTournamentFinished(data.id)
      await tournamentListHttp.fetch()
      const tournament = tournamentsStore.getTournament(data.id)
      if (tournament && !tournament.settings.is_fast) {
        dialog.open('tournamentWinner', { id: data.id })
      }
    }
  })

  onBeforeMount(async () => {
    try {
      if (!isBot) {
        autoDetectLocale()
      }
    } catch (error) {
      console.error('Auto Detect Locale', error)
    }

    try {
      if (isTelegram()) {
        const scriptNode = document.createElement('script')
        scriptNode.src = 'https://telegram.org/js/telegram-web-app.js'
        scriptNode.onload = async () => {
          window.Telegram?.WebApp.enableClosingConfirmation()
          window.Telegram?.WebApp.expand()

          await app.$di.user.loginTelegram()
          // checkTimeOffset()
        }
        document.body.appendChild(scriptNode)
      } else {
        const authTokenQuery = route.query[Query.AuthToken]
        await app.$di.root.init({
          initParams: {
            auth_token: typeof authTokenQuery === 'string' ? authTokenQuery : undefined,
          },
        })
        // checkTimeOffset()
      }

      const streamerMode = storageService.get('streamer-mode')
      const volume = storageService.get('volume')
      const xpMode = storageService.get('xp-mode')
      if (volume) {
        rootStore.setVolume(Number(JSON.parse(volume)))
      }
      if (streamerMode) {
        rootStore.setStreamerMode(Boolean(JSON.parse(streamerMode)))
      }
      if (xpMode) {
        rootStore.setXpMode(Boolean(JSON.parse(xpMode)))
      }
    } catch {
      console.error('Fail init request')
    }
  })

  if (import.meta.client) {
    cookieService.delete('user-locale')
  }

  async function autoDetectLocale(): Promise<void> {
    const saved = cookieService.get('i18n_lang')
    const pathLocale = getPathLocale(availableLocale)
    if (pathLocale) {
      if (locale.value !== pathLocale || saved !== pathLocale) {
        setLocale(pathLocale)
      }
      return
    }
    // 1. cookie всегда главный
    if (saved && availableLocale.includes(saved)) {
      if (locale.value !== saved) {
        setLocale(saved)
      }
      return
    }
    // ждём загрузку пользователя
    await new Promise((resolve) => {
      requestAnimationFrame(() => checkUserLoading(resolve))
    })
    // 3. автоопределение
    let fallback = 'en'
    const country = rootStore.config?.current_country
    if (country && countryList[country]) {
      const possibleLocales = countryList[country]
      const matched =
        possibleLocales.find((l) => l !== 'en' && availableLocale.includes(l)) ??
        possibleLocales.find((l) => availableLocale.includes(l))
      if (matched) {
        fallback = matched
      }
    }
    const browserLocale = detectLocale(availableLocale, fallback)
    const detected = browserLocale === 'en' ? fallback : browserLocale
    if (detected !== locale.value) {
      setLocale(detected)
    }
  }

  function checkUserLoading(resolve: (value?: unknown) => void): void {
    if (userStore.isLoading) {
      requestAnimationFrame(() => checkUserLoading(resolve))
      return
    }
    resolve()
  }

  let lastExitIntentTime = 0

  async function handleExitIntent(event: Event): Promise<void> {
    const now = Date.now()

    if (lastExitIntentTime > 0 && now - lastExitIntentTime < 120000) {
      return
    }

    if (event.type === 'mouseleave' && (event as MouseEvent).clientY > 10) {
      return
    }

    if (event.type === 'visibilitychange' && !document.hidden) {
      return
    }

    lastExitIntentTime = now

    await app.$di.user.sendMarketingTrigger('user_close_tab_browser')
  }

  function handleFirstClick(): void {
    rootStore.setCanPlaySound(true)
    document.removeEventListener('click', handleFirstClick)
  }

  function refreshSafeArea(): void {
    const cs = window.getComputedStyle(document.body)
    rootStore.setEnvSafeArea({
      bottom: isPWA() ? 0 : parseFloat(cs.getPropertyValue('--brick-env-safe-area-inset-bottom')) || 0,
      left: parseFloat(cs.getPropertyValue('--brick-env-safe-area-inset-left')) || 0,
      right: parseFloat(cs.getPropertyValue('--brick-env-safe-area-inset-right')) || 0,
      top: parseFloat(cs.getPropertyValue('--brick-env-safe-area-inset-top')) || 0,
    })
  }

  onMounted(async () => {
    document.addEventListener('click', handleFirstClick)
    document.addEventListener('mouseleave', handleExitIntent)
    document.addEventListener('visibilitychange', handleExitIntent)
    window.addEventListener('beforeunload', handleExitIntent)

    refreshSafeArea()
    restoreTimezone()
    try {
      app.$di.user.setUserVisit()
    } catch (error) {
      console.error(error)
    }
    initValibotMessages(t)

    if (route.query.devtool === null) {
      setLocale('debug')
    }
    if (isPWA() && userStore.isAuth) {
      app.$di.user.platformPreferred()
    }

    // App version
    const userLastVersion = storageService.get('app-version') ?? ''
    const appVersion = await getAppVersion()
    console.info('[App] Web version:', appVersion)
    console.info('[App] User version:', userLastVersion)

    if (isEmpty(userLastVersion)) {
      storageService.set('app-version', appVersion)
    } else if (userLastVersion !== appVersion) {
      console.info('[App] New app version detected, clearing storage...')
      storageService.set('app-version', appVersion)

      if (isPWA()) {
        setTimeout(() => {
          storageService.set('app-version', appVersion)
          // location.reload()
        }, 300)
      }
    }

    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready
      registration.active?.postMessage({ type: 'GET_VERSION' })

      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'SW_VERSION') {
          console.info('[App] SW version:', event.data.version)
        }
      })
    }
  })

  watch(
    () => route.fullPath,
    (_, oldPath) => {
      const isTelegram = /tgWebAppData/.test(oldPath)
      if (isTelegram) {
        window.Telegram?.WebApp.BackButton.show()
        window.Telegram?.WebApp.BackButton.onClick(() => router.go(-1))
      }
    },
    {
      once: true,
    },
  )

  watch(
    () => rootStore.config?.current_country,
    (country) => {
      if (import.meta.client && country) {
        restoreTimezone()
      }
    },
  )

  // Query actions
  async function removeQuery(name: string): Promise<string> {
    const value = route.query[name]
    await router.replace({ query: { ...route.query, [name]: undefined } })
    return String(value)
  }
  let queryPromise: Promise<undefined> | undefined = undefined
  watch(
    () => [route.fullPath, userStore.isLoading, userStore.isAuth, userStore.isCached],
    async () => {
      if (userStore.isLoading || userStore.isCached) {
        return
      }

      // Await until previous query watcher done
      await queryPromise
      let resolver: (() => void) | undefined
      queryPromise = new Promise((resolve) => {
        resolver = resolve as () => void
      })

      const stag = route.query.stag
      const clickId = route.query.click_id
      const campaignAlias = route.query.campaign_alias

      if (typeof stag === 'string') {
        cookieService.set('stag', stag, 7)
      }
      if (cookieService.get('stag')?.startsWith('113601_')) {
        userStore.setBrazilBonusStag(true)
      }
      if (typeof clickId === 'string') {
        cookieService.set('click_id', clickId, 7)
      }
      if (typeof campaignAlias === 'string') {
        cookieService.set('campaign_alias', campaignAlias, 7)
      }

      if (route.query[Query.Flow]) {
        const flow = await removeQuery(Query.Flow)
        sessionStorage.setItem(Query.Flow, flow)
      }
      if (route.query[Query.Support]) {
        rootStore.toggleIntercom()
        await removeQuery(Query.Support)
      }
      if (route.query[Query.SupportMessage]) {
        rootStore.toggleIntercom('message')
        await removeQuery(Query.SupportMessage)
      }
      if (route.query[Query.Conversation]) {
        const conversationId = String(route.query[Query.Conversation])
        rootStore.showIntercomConversation(conversationId)
        await removeQuery(Query.Conversation)
      }
      if (route.query[Query.WheelSpins] !== undefined) {
        if (userStore.isAuth) {
          await dialog.open('fortuneWheel')
        } else {
          await dialog.open('signUp')
        }
        await removeQuery(Query.WheelSpins)
      }
      if (route.query[Query.Feedback] !== undefined) {
        if (userStore.isAuth) {
          await dialog.open('feedback')
        } else {
          await dialog.open('signUp')
        }
        await removeQuery(Query.Feedback)
      }
      if (route.query[Query.Research] !== undefined) {
        await dialog.open('castDev')
        await removeQuery(Query.Research)
      }
      if (route.query[Query.RewardPage] !== undefined) {
        const tabId = String(route.query[Query.RewardPage])
        await dialog.open('rewardsPage', { tabId: tabId || 'claim' })
        await removeQuery(Query.RewardPage)
      }

      if (route.query[Query.WrongCuit] === '1') {
        notify.create({
          description: 'check_kyc_input_fields',
          title: 'error',
          type: 'error',
        })
        if (userStore.isAuth) {
          await dialog.open('kycPartial', {
            documentRequirements: ['CUIT'],
            requiredFields: ['documents'],
          })
        }
        await removeQuery(Query.WrongCuit)
      }

      const indianFortuneWasOpen = cookieService.get('indian_fortune_wheel_was_open')
      const latinFortuneWasOpen = cookieService.get('latin_fortune_wheel_was_open')
      if (rootStore.config?.wheel_spin?.wheel_spin_india_with_bonus && !indianFortuneWasOpen) {
        await dialog.open('indianFortuneWheel')
      }
      if (rootStore.config?.wheel_spin?.wheel_spin_latin_with_bonus && !latinFortuneWasOpen) {
        await dialog.open('latinFortuneWheel')
      }

      if (userStore.isAuth && (route.query[Query.EmailConfirm] || route.query[Query.EmailConfirmSignUp])) {
        const emailConfirm = String(route.query[Query.EmailConfirm])
        const emailConfirmSignUp = String(route.query[Query.EmailConfirmSignUp])
        const emailConfirmCode = emailConfirm || emailConfirmSignUp
        const type = emailConfirm ? 'add_email' : 'signup'

        await dialog.open('emailConfirm', {
          code: emailConfirmCode,
          type,
        })
        await removeQuery(Query.EmailConfirm)
        await removeQuery(Query.EmailConfirmSignUp)
      }

      if (route.query[Query.ReferralPromocodeDeposit]) {
        const promocode = String(route.query[Query.ReferralPromocodeDeposit])
        cookieService.set('referral_promocode', promocode, 7)
        if (route.query[Query.EmailToken]) {
          const emailToken = await removeQuery(Query.EmailToken)
          await confirmEmailHttp.fetch({ redirect: 'manual', token: emailToken, type: 'add_email' })
        }

        await removeQuery(Query.ReferralPromocodeDeposit)
      }
      if (route.query[Query.ReferralPromocode]) {
        const referralPromocode = String(route.query[Query.ReferralPromocode])
        if (referralPromocode && !userStore.isAuth) {
          cookieService.set('referral_promocode', referralPromocode, 7)
          await dialog.open('signUp')
        }

        if (route.query[Query.EmailToken]) {
          const emailToken = String(route.query[Query.EmailToken])
          await confirmEmailHttp.fetch({ redirect: 'manual', token: emailToken, type: 'add_email' })
        }

        await removeQuery(Query.EmailToken)
      }

      if (route.query[Query.Promocode] !== undefined || cookieService.get('bonus_promocode')) {
        if (userStore.isAuth) {
          const queryPromocode = String(route.query[Query.Promocode])
          const promocode = queryPromocode || cookieService.get('bonus_promocode')

          if (route.query[Query.EmailToken]) {
            const emailToken = await removeQuery(Query.EmailToken)
            await confirmEmailHttp.fetch({ redirect: 'manual', token: emailToken, type: 'add_email' })
          }
          await dialog.open('promocode', { promocode })
          await removeQuery(Query.Promocode)
        } else {
          await dialog.open('signIn')
        }
      }

      if (route.query[Query.BonusActivate] !== undefined) {
        const promocode = String(route.query[Query.BonusActivate])
        if (userStore.isAuth) {
          const result = await app.$di.user.setPromocode(promocode)
          if (result.status === 'success') {
            notify.create({
              title: t('The code was used successfully!'),
            })
            if (result.user_bonus) {
              userStore.setBonus(result.user_bonus)
              rootStore.removeSpinWithBonus()
            }
          }
        } else {
          sessionStorage.setItem('AuthRedirect', route.fullPath)
        }
        await removeQuery(Query.BonusActivate)
      }

      if (route.query[Query.Kyc] !== undefined) {
        if (userStore.isAuth) {
          const requiredFields = ['first_name', 'last_name', 'birthday_date', 'phone', 'city', 'street', 'zip']
          await dialog.open('kyc', requiredFields)
        } else {
          sessionStorage?.setItem('open-kyc', '1')
          await dialog.open('signUp')
        }
        await removeQuery(Query.Kyc)
      }
      if (userStore.isAuth && sessionStorage?.getItem('open-kyc')) {
        sessionStorage?.removeItem('open-kyc')
        const requiredFields = ['first_name', 'last_name', 'birthday_date', 'phone', 'city', 'street', 'zip']
        await dialog.open('kyc', requiredFields)
      }

      if (route.query[Query.Auth] && !userStore.isAuth) {
        const value = String(route.query[Query.Auth])
        if (value === 'register') {
          await dialog.open('signUp')
        }
        if (value === 'login') {
          await dialog.open('signIn')
        }
        await removeQuery(Query.Auth)
      }
      if (route.query[Query.ReferralUserBonus] && !userStore.isAuth) {
        const value = String(route.query[Query.ReferralUserBonus])
        await dialog.open('signUp', { promocode: value })
        await removeQuery(Query.ReferralUserBonus)
      }
      if (route.query[Query.Bonus] && !userStore.isAuth) {
        const value = String(route.query[Query.Bonus])
        await dialog.open('signUp', { promocode: value })
        await removeQuery(Query.Bonus)
      }
      if (route.query[Query.BonusGift]) {
        const code = String(route.query[Query.BonusGift])
        await dialog.open('bonusGift', { code })
        await removeQuery(Query.BonusGift)
      }
      if (route.query[Query.EmailSubscription] !== undefined) {
        await dialog.open('emailWithConfirm')
        await removeQuery(Query.EmailSubscription)
      }
      if (route.query[Query.Quiz] !== undefined) {
        await dialog.open('quiz')
        await removeQuery(Query.Quiz)
      }
      if (route.query[Query.Rewards] !== undefined) {
        await dialog.open('rewards')
        await removeQuery(Query.Rewards)
      }
      if (route.query[Query.BecameVip] !== undefined) {
        await dialog.open('becameVip')
        await removeQuery(Query.BecameVip)
      }

      resolver?.()
    },
    {
      immediate: true,
    },
  )

  onBeforeUnmount(() => {
    document.removeEventListener('mouseleave', handleExitIntent)
    document.removeEventListener('visibilitychange', handleExitIntent)
    window.removeEventListener('beforeunload', handleExitIntent)
  })

  if (import.meta.server) {
    const event = useRequestEvent()

    if (event) {
      const date = new Date()

      const formatted = `${[
        String(date.getUTCDate()).padStart(2, '0'),
        String(date.getUTCMonth() + 1).padStart(2, '0'),
        date.getUTCFullYear(),
      ].join('.')} ${[
        String(date.getUTCHours()).padStart(2, '0'),
        String(date.getUTCMinutes()).padStart(2, '0'),
        String(date.getUTCSeconds()).padStart(2, '0'),
      ].join(':')}`
      event.node?.res?.setHeader('X-Render-Time', formatted)
    }
  }
</script>
